import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Link } from 'react-router-dom';
import { Settings, TrendingUp, TrendingDown, Activity, DollarSign, Play, Square, AlertCircle, User, Wallet, LogOut, CreditCard, Shield, Gauge, BarChart3, Grid3X3, ArrowUpCircle, ArrowDownCircle, Zap, HelpCircle, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import useWebSocket from '@/hooks/useWebSocket';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map strategy IDs to display names
const getStrategyName = (strategyId) => {
  const strategyMap = {
    'mean_reversion_rsi': 'Mean Reversion (RSI)',
    'trend_following': 'Trend Following (EMA)',
    'breakout': 'Breakout Strategy',
    'grid_trading': 'Grid Trading'
  };
  return strategyMap[strategyId] || strategyId || 'Not Selected';
};

// Map symbols to display names
const getCryptoName = (symbol) => {
  if (!symbol) return 'Crypto';
  
  const symbolMap = {
    'tBTCUSD': 'Bitcoin',
    'tETHUSD': 'Ethereum',
    'tLTCUSD': 'Litecoin',
    'tXRPUSD': 'Ripple',
    'tBCHUSD': 'Bitcoin Cash',
    'tEOSUSD': 'EOS',
    'tXLMUSD': 'Stellar',
    'tTRXUSD': 'Tron',
    'tADAUSD': 'Cardano',
    'tXMRUSD': 'Monero',
    'tDOGE:USD': 'Dogecoin',
    'tPEPE:USD': 'Pepe',
    'tSHIB:USD': 'Shiba Inu',
    'tSOL:USD': 'Solana',
    'tDOT:USD': 'Polkadot',
    'tMATIC:USD': 'Polygon',
    'tAVAX:USD': 'Avalanche'
  };
  
  // Check direct match first
  if (symbolMap[symbol]) return symbolMap[symbol];
  
  // Extract coin name from symbol (handle both formats: tBTCUSD and tDOGE:USD)
  let coinName = symbol.replace('t', '').replace(':USD', '').replace('USD', '');
  return coinName;
};

// Format price based on its magnitude
const formatPrice = (price) => {
  if (!price || price === 0) return '...';
  
  // For very small prices (< $0.01), show more decimals
  if (price < 0.00001) {
    return `$${price.toFixed(10)}`;
  } else if (price < 0.0001) {
    return `$${price.toFixed(8)}`;
  } else if (price < 0.01) {
    return `$${price.toFixed(6)}`;
  } else if (price < 1) {
    return `$${price.toFixed(4)}`;
  } else if (price < 100) {
    return `$${price.toFixed(2)}`;
  } else {
    return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

// Strategy Indicators Component
const StrategyIndicators = ({ indicators, strategy, currentPrice }) => {
  if (!indicators || !strategy) return null;
  
  const strategyType = indicators.strategy_type || strategy;
  
  // Mean Reversion RSI Indicators
  if (strategyType === 'mean_reversion_rsi') {
    const rsi = indicators.rsi;
    const oversold = indicators.rsi_oversold || 30;
    const overbought = indicators.rsi_overbought || 70;
    
    const getRsiColor = () => {
      if (!rsi) return 'text-gray-400';
      if (rsi <= oversold) return 'text-green-400';
      if (rsi >= overbought) return 'text-red-400';
      return 'text-blue-300';
    };
    
    const getRsiStatus = () => {
      if (!rsi) return 'Calculating...';
      if (rsi <= oversold) return 'OVERSOLD - Buy Zone';
      if (rsi >= overbought) return 'OVERBOUGHT - Sell Zone';
      return 'Neutral';
    };
    
    return (
      <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="strategy-indicators-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Gauge className="w-5 h-5 text-blue-400" />
              RSI Indicator
            </CardTitle>
            <Badge className={`${getRsiColor()} bg-opacity-20`}>{getRsiStatus()}</Badge>
          </div>
          <CardDescription className="text-blue-200">Relative Strength Index measures momentum</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* RSI Value Display */}
            <div className="text-center">
              <div className={`text-5xl font-bold ${getRsiColor()}`} data-testid="rsi-value">
                {rsi ? rsi.toFixed(1) : '...'}
              </div>
              <div className="text-sm text-blue-200 mt-1">Current RSI</div>
            </div>
            
            {/* RSI Progress Bar */}
            <div className="relative pt-4">
              <div className="flex justify-between text-xs text-blue-300 mb-1">
                <span>Oversold ({oversold})</span>
                <span>Neutral (50)</span>
                <span>Overbought ({overbought})</span>
              </div>
              <div className="h-3 bg-gradient-to-r from-green-500 via-gray-500 to-red-500 rounded-full relative">
                {rsi && (
                  <div 
                    className="absolute top-0 w-3 h-3 bg-white rounded-full border-2 border-blue-500 shadow-lg transform -translate-x-1/2"
                    style={{ left: `${Math.min(Math.max(rsi, 0), 100)}%` }}
                  />
                )}
              </div>
              <div className="flex justify-between text-xs text-blue-400 mt-1">
                <span>0</span>
                <span>100</span>
              </div>
            </div>
            
            {/* Signal Explanation */}
            <div className="p-3 bg-white/5 rounded-lg text-sm">
              {rsi <= oversold && (
                <p className="text-green-300">
                  <ArrowUpCircle className="inline w-4 h-4 mr-1" />
                  RSI below {oversold} indicates oversold conditions. Bot may buy on confirmation.
                </p>
              )}
              {rsi >= overbought && (
                <p className="text-red-300">
                  <ArrowDownCircle className="inline w-4 h-4 mr-1" />
                  RSI above {overbought} indicates overbought conditions. Bot may sell position.
                </p>
              )}
              {rsi > oversold && rsi < overbought && (
                <p className="text-blue-200">
                  RSI in neutral zone ({oversold}-{overbought}). Waiting for oversold/overbought conditions.
                </p>
              )}
              {!rsi && (
                <p className="text-gray-400">Collecting price data to calculate RSI...</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Breakout Strategy Indicators
  if (strategyType === 'breakout') {
    const donchianUpper = indicators.donchian_upper;
    const donchianLower = indicators.donchian_lower;
    const isSqueeze = indicators.is_squeeze;
    const isVolumeSpike = indicators.is_volume_spike;
    const bbWidth = indicators.bb_width_pct;
    
    const getPricePosition = () => {
      if (!currentPrice || !donchianUpper || !donchianLower) return 50;
      const range = donchianUpper - donchianLower;
      if (range === 0) return 50;
      return Math.min(Math.max(((currentPrice - donchianLower) / range) * 100, 0), 100);
    };
    
    const getBreakoutStatus = () => {
      if (!currentPrice || !donchianUpper || !donchianLower) return { status: 'Calculating', color: 'text-gray-400' };
      if (currentPrice > donchianUpper) return { status: 'BREAKOUT UP!', color: 'text-green-400' };
      if (currentPrice < donchianLower) return { status: 'BREAKDOWN!', color: 'text-red-400' };
      if (isSqueeze) return { status: 'SQUEEZE - Preparing', color: 'text-yellow-400' };
      return { status: 'In Range', color: 'text-blue-300' };
    };
    
    const breakoutStatus = getBreakoutStatus();
    
    return (
      <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="strategy-indicators-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              Breakout Indicators
            </CardTitle>
            <Badge className={`${breakoutStatus.color} bg-opacity-20`}>{breakoutStatus.status}</Badge>
          </div>
          <CardDescription className="text-blue-200">Donchian Channels with Bollinger Squeeze detection</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Channel Visualization */}
            <div className="relative">
              <div className="flex justify-between text-xs text-blue-300 mb-1">
                <span>Lower: {donchianLower ? formatPrice(donchianLower) : '...'}</span>
                <span>Upper: {donchianUpper ? formatPrice(donchianUpper) : '...'}</span>
              </div>
              <div className="h-8 bg-gradient-to-r from-red-500/30 via-blue-500/30 to-green-500/30 rounded-lg relative border border-white/20">
                {/* Channel boundaries */}
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 rounded-l-lg" />
                <div className="absolute right-0 top-0 bottom-0 w-1 bg-green-500 rounded-r-lg" />
                
                {/* Current price marker */}
                {currentPrice && donchianUpper && donchianLower && (
                  <div 
                    className="absolute top-1/2 w-4 h-4 bg-white rounded-full border-2 border-blue-500 shadow-lg transform -translate-x-1/2 -translate-y-1/2 z-10"
                    style={{ left: `${getPricePosition()}%` }}
                  >
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-white whitespace-nowrap bg-blue-600 px-1 rounded">
                      {formatPrice(currentPrice)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Status Indicators */}
            <div className="grid grid-cols-2 gap-3">
              <div className={`p-3 rounded-lg ${isSqueeze ? 'bg-yellow-500/20 border border-yellow-500/50' : 'bg-white/5'}`}>
                <div className="flex items-center gap-2">
                  <Zap className={`w-4 h-4 ${isSqueeze ? 'text-yellow-400' : 'text-gray-500'}`} />
                  <span className={`text-sm font-medium ${isSqueeze ? 'text-yellow-300' : 'text-gray-400'}`}>
                    BB Squeeze
                  </span>
                </div>
                <div className="text-xs mt-1 text-blue-200">
                  {bbWidth ? `Width: ${bbWidth.toFixed(2)}%` : 'Calculating...'}
                </div>
              </div>
              
              <div className={`p-3 rounded-lg ${
                isVolumeSpike === true ? 'bg-purple-500/20 border border-purple-500/50' : 
                isVolumeSpike === null ? 'bg-gray-500/10 border border-gray-500/30' : 'bg-white/5'
              }`}>
                <div className="flex items-center gap-2">
                  <BarChart3 className={`w-4 h-4 ${
                    isVolumeSpike === true ? 'text-purple-400' : 
                    isVolumeSpike === null ? 'text-gray-500' : 'text-gray-500'
                  }`} />
                  <span className={`text-sm font-medium ${
                    isVolumeSpike === true ? 'text-purple-300' : 
                    isVolumeSpike === null ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    Volume Spike
                  </span>
                </div>
                <div className="text-xs mt-1 text-blue-200">
                  {isVolumeSpike === true ? 'Above average!' : 
                   isVolumeSpike === null ? 'No volume data' : 'Normal'}
                </div>
              </div>
            </div>
            
            {/* Signal Explanation */}
            <div className="p-3 bg-white/5 rounded-lg text-sm">
              {currentPrice > donchianUpper && (
                <p className="text-green-300">
                  <ArrowUpCircle className="inline w-4 h-4 mr-1" />
                  Price broke above channel! {isSqueeze && 'Squeeze breakout - strong signal!'} {isVolumeSpike && 'Volume confirms!'}
                </p>
              )}
              {currentPrice < donchianLower && (
                <p className="text-red-300">
                  <ArrowDownCircle className="inline w-4 h-4 mr-1" />
                  Price broke below channel! {isSqueeze && 'Squeeze breakdown!'} Consider closing longs.
                </p>
              )}
              {isSqueeze && currentPrice >= donchianLower && currentPrice <= donchianUpper && (
                <p className="text-yellow-300">
                  <Zap className="inline w-4 h-4 mr-1" />
                  Low volatility squeeze detected! Breakout imminent - watch for channel break.
                </p>
              )}
              {!isSqueeze && currentPrice >= donchianLower && currentPrice <= donchianUpper && (
                <p className="text-blue-200">
                  Price within range. Waiting for breakout above {formatPrice(donchianUpper)} or below {formatPrice(donchianLower)}.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Trend Following Indicators
  if (strategyType === 'trend_following') {
    const fastEma = indicators.fast_ema;
    const slowEma = indicators.slow_ema;
    const adx = indicators.adx;
    const adxThreshold = indicators.adx_threshold || 25;
    const emaSeparation = indicators.ema_separation;
    
    const getTrendStatus = () => {
      if (!fastEma || !slowEma) return { status: 'Calculating', color: 'text-gray-400', direction: null };
      const isBullish = fastEma > slowEma;
      const trendStrong = adx && adx >= adxThreshold;
      
      if (isBullish && trendStrong) return { status: 'STRONG UPTREND', color: 'text-green-400', direction: 'up' };
      if (!isBullish && trendStrong) return { status: 'STRONG DOWNTREND', color: 'text-red-400', direction: 'down' };
      if (isBullish) return { status: 'Weak Uptrend', color: 'text-green-300', direction: 'up' };
      return { status: 'Weak Downtrend', color: 'text-red-300', direction: 'down' };
    };
    
    const trendStatus = getTrendStatus();
    
    return (
      <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="strategy-indicators-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              {trendStatus.direction === 'up' ? (
                <TrendingUp className="w-5 h-5 text-green-400" />
              ) : trendStatus.direction === 'down' ? (
                <TrendingDown className="w-5 h-5 text-red-400" />
              ) : (
                <Activity className="w-5 h-5 text-blue-400" />
              )}
              Trend Indicators
            </CardTitle>
            <Badge className={`${trendStatus.color} bg-opacity-20`}>{trendStatus.status}</Badge>
          </div>
          <CardDescription className="text-blue-200">EMA Crossover with ADX trend strength</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* EMA Values */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <div className="text-xs text-blue-300 mb-1">Fast EMA (50)</div>
                <div className={`text-2xl font-bold ${fastEma > slowEma ? 'text-green-400' : 'text-red-400'}`}>
                  {fastEma ? formatPrice(fastEma) : '...'}
                </div>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <div className="text-xs text-blue-300 mb-1">Slow EMA (200)</div>
                <div className="text-2xl font-bold text-blue-100">
                  {slowEma ? formatPrice(slowEma) : '...'}
                </div>
              </div>
            </div>
            
            {/* EMA Separation */}
            {emaSeparation !== null && emaSeparation !== undefined && (
              <div className="p-3 bg-white/5 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-blue-200">EMA Separation</span>
                  <span className={`font-bold ${emaSeparation > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {emaSeparation > 0 ? '+' : ''}{emaSeparation.toFixed(2)}%
                  </span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${emaSeparation > 0 ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(Math.abs(emaSeparation) * 10, 100)}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* ADX Gauge */}
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-blue-200">ADX (Trend Strength)</span>
                <span className={`font-bold ${adx >= adxThreshold ? 'text-green-400' : 'text-yellow-400'}`}>
                  {adx ? adx.toFixed(1) : '...'}
                </span>
              </div>
              <div className="relative">
                <Progress value={adx ? Math.min(adx, 100) : 0} className="h-3" />
                <div 
                  className="absolute top-0 h-3 w-0.5 bg-white"
                  style={{ left: `${adxThreshold}%` }}
                  title={`Threshold: ${adxThreshold}`}
                />
              </div>
              <div className="flex justify-between text-xs text-blue-400 mt-1">
                <span>Weak (0)</span>
                <span>Strong ({adxThreshold})</span>
                <span>Very Strong (50+)</span>
              </div>
            </div>
            
            {/* Signal Explanation */}
            <div className="p-3 bg-white/5 rounded-lg text-sm">
              {fastEma > slowEma && adx >= adxThreshold && (
                <p className="text-green-300">
                  <ArrowUpCircle className="inline w-4 h-4 mr-1" />
                  Strong uptrend confirmed! Fast EMA above Slow EMA with ADX {adx?.toFixed(1)} confirming momentum.
                </p>
              )}
              {fastEma < slowEma && adx >= adxThreshold && (
                <p className="text-red-300">
                  <ArrowDownCircle className="inline w-4 h-4 mr-1" />
                  Strong downtrend! Consider closing long positions or shorting.
                </p>
              )}
              {adx && adx < adxThreshold && (
                <p className="text-yellow-300">
                  ADX below {adxThreshold} indicates weak trend. Bot waiting for stronger momentum.
                </p>
              )}
              {!adx && (
                <p className="text-gray-400">Collecting data to calculate trend strength...</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Grid Trading Indicators
  if (strategyType === 'grid_trading') {
    const gridCenter = indicators.grid_center;
    const gridSpacing = indicators.grid_spacing;
    const nearestBuy = indicators.nearest_buy_level;
    const nearestSell = indicators.nearest_sell_level;
    const isTrending = indicators.is_trending;
    const trendDirection = indicators.trend_direction;
    const emaDistance = indicators.distance_from_ema_pct;
    const triggeredLevels = indicators.triggered_levels || 0;
    const totalLevels = indicators.total_grid_levels || 0;
    
    const getGridStatus = () => {
      if (isTrending) return { status: 'GRID DISABLED', color: 'text-red-400' };
      if (!gridCenter) return { status: 'Initializing', color: 'text-gray-400' };
      return { status: 'GRID ACTIVE', color: 'text-green-400' };
    };
    
    const gridStatus = getGridStatus();
    
    return (
      <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="strategy-indicators-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-cyan-400" />
              Grid Trading
            </CardTitle>
            <Badge className={`${gridStatus.color} bg-opacity-20`}>{gridStatus.status}</Badge>
          </div>
          <CardDescription className="text-blue-200">Buy low, sell high within a price range</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Grid Levels Visualization */}
            {gridCenter && nearestBuy && nearestSell && (
              <div className="relative">
                <div className="flex justify-between text-xs text-blue-300 mb-1">
                  <span className="text-green-400">Next Buy: {formatPrice(nearestBuy)}</span>
                  <span className="text-red-400">Next Sell: {formatPrice(nearestSell)}</span>
                </div>
                <div className="h-12 bg-gradient-to-t from-green-500/20 via-blue-500/10 to-red-500/20 rounded-lg relative border border-white/20">
                  {/* Grid center line */}
                  <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-blue-400" />
                  
                  {/* Buy zone */}
                  <div className="absolute bottom-0 left-0 right-0 h-1/3 border-t border-dashed border-green-500/50" />
                  
                  {/* Sell zone */}
                  <div className="absolute top-0 left-0 right-0 h-1/3 border-b border-dashed border-red-500/50" />
                  
                  {/* Current price marker */}
                  {currentPrice && nearestBuy && nearestSell && (
                    <div 
                      className="absolute left-1/2 w-3 h-3 bg-white rounded-full border-2 border-yellow-500 shadow-lg transform -translate-x-1/2"
                      style={{ 
                        top: `${Math.max(10, Math.min(90, 50 - ((currentPrice - gridCenter) / gridSpacing) * 20))}%` 
                      }}
                    />
                  )}
                </div>
                <div className="text-center text-xs text-blue-300 mt-1">
                  Grid Center: {formatPrice(gridCenter)}
                </div>
              </div>
            )}
            
            {/* Grid Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <div className="text-xs text-blue-300">Spacing</div>
                <div className="text-lg font-bold text-white">
                  {gridSpacing ? formatPrice(gridSpacing) : '...'}
                </div>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <div className="text-xs text-blue-300">Triggered</div>
                <div className="text-lg font-bold text-cyan-400">
                  {triggeredLevels}/{totalLevels}
                </div>
              </div>
              <div className="text-center p-2 bg-white/5 rounded-lg">
                <div className="text-xs text-blue-300">EMA Dist.</div>
                <div className={`text-lg font-bold ${Math.abs(emaDistance || 0) > 3 ? 'text-red-400' : 'text-green-400'}`}>
                  {emaDistance ? `${emaDistance > 0 ? '+' : ''}${emaDistance.toFixed(1)}%` : '...'}
                </div>
              </div>
            </div>
            
            {/* Trend Warning */}
            {isTrending && (
              <Alert className="bg-red-500/20 border-red-500/50">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-200">
                  {trendDirection === 'BULLISH' ? 'Strong bullish' : 'Strong bearish'} trend detected! 
                  Grid trading disabled to protect against trending losses.
                </AlertDescription>
              </Alert>
            )}
            
            {/* Signal Explanation */}
            <div className="p-3 bg-white/5 rounded-lg text-sm">
              {!isTrending && gridCenter && (
                <p className="text-green-300">
                  <Grid3X3 className="inline w-4 h-4 mr-1" />
                  Grid active! Bot will buy at {formatPrice(nearestBuy)} and sell at {formatPrice(nearestSell)}.
                </p>
              )}
              {!gridCenter && !isTrending && (
                <p className="text-blue-200">
                  Initializing grid around current price. Grid will activate shortly.
                </p>
              )}
              {isTrending && (
                <p className="text-yellow-300">
                  Waiting for market to return to range-bound conditions (within 3% of EMA).
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Fallback for unknown strategy
  return null;
};

const Dashboard = () => {
  const { user, wallet, logout, refreshWallet } = useAuth();
  const [botStatus, setBotStatus] = useState(null);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [botSettings, setBotSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [fetchCount, setFetchCount] = useState(0); // Debug counter

  // WebSocket connection for real-time updates (may not work through all proxies)
  const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws/bot-status';
  const { lastMessage, isConnected } = useWebSocket(wsUrl);

  // Track the last known running state timestamp to prevent flickering
  const lastRunningStateRef = useRef({ is_running: null, timestamp: 0 });

  // Smart state update function that prevents flickering
  const updateBotStatus = (newStatus, source = 'unknown') => {
    if (!newStatus) return;
    
    const now = Date.now();
    const lastState = lastRunningStateRef.current;
    
    // If running state changed, require a 500ms debounce to prevent flickering
    // This handles the case where different pods return different states
    if (lastState.is_running !== null && lastState.is_running !== newStatus.is_running) {
      const timeSinceLastChange = now - lastState.timestamp;
      if (timeSinceLastChange < 500) {
        // Ignore rapid state changes - keep current state
        console.log(`[${source}] Ignoring rapid state change: ${lastState.is_running} -> ${newStatus.is_running} (${timeSinceLastChange}ms)`);
        return;
      }
    }
    
    // Update the reference with new running state
    lastRunningStateRef.current = {
      is_running: newStatus.is_running,
      timestamp: now
    };
    
    setBotStatus(newStatus);
    setLastUpdate(new Date());
  };

  // Update bot status from WebSocket when available (prioritize WebSocket)
  useEffect(() => {
    if (lastMessage && lastMessage.type === 'bot_status') {
      updateBotStatus(lastMessage.data, 'websocket');
    }
  }, [lastMessage]);

  // Fetch bot settings on mount
  useEffect(() => {
    fetchBotSettings();
  }, []);

  // Refresh wallet when page regains focus (e.g., returning from payment)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshWallet();
      }
    };
    
    const handleFocus = () => {
      refreshWallet();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshWallet]);

  const fetchBotSettings = async () => {
    try {
      const response = await axios.get(`${API}/bot/settings`);
      setBotSettings(response.data.data);
    } catch (error) {
      console.error('Error fetching bot settings:', error);
    }
  };

  // Auto-refresh: Poll for status updates every 2 seconds (reduced from 1s to prevent excessive DB reads)
  // Backend now reads from database ensuring consistency across Kubernetes pods
  useEffect(() => {
    // Define fetch function inside useEffect to avoid stale closure
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API}/bot/status`);
        updateBotStatus(response.data.data, 'rest-poll');
        setFetchCount(prev => prev + 1); // Increment counter
      } catch (error) {
        console.error('Error fetching bot status:', error);
      }
    };

    const fetchTrades = async () => {
      try {
        const response = await axios.get(`${API}/bot/trades`);
        setTradeHistory(response.data.data || []);
      } catch (error) {
        console.error('Error fetching trade history:', error);
      }
    };

    // Initial fetch
    fetchStatus();
    fetchTrades();

    const statusInterval = setInterval(fetchStatus, 2000); // Poll every 2 seconds (database-backed, consistent)
    const tradeInterval = setInterval(fetchTrades, 10000); // Trade history every 10 seconds
    const walletInterval = setInterval(refreshWallet, 30000); // Wallet refresh every 30 seconds

    return () => {
      clearInterval(statusInterval);
      clearInterval(tradeInterval);
      clearInterval(walletInterval);
    };
  }, [refreshWallet]); // Include refreshWallet in dependencies

  const fetchBotStatus = async () => {
    try {
      const response = await axios.get(`${API}/bot/status`);
      setBotStatus(response.data.data);
      setLastUpdate(new Date());
      // Debug log
      console.log('Fetched status, price:', response.data.data?.current_price);
    } catch (error) {
      console.error('Error fetching bot status:', error);
    }
  };

  const fetchTradeHistory = async () => {
    try {
      const response = await axios.get(`${API}/bot/trades`);
      setTradeHistory(response.data.data);
    } catch (error) {
      console.error('Error fetching trade history:', error);
    }
  };

  const handleBotControl = async (action) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/bot/control`, { action });
      toast.success(response.data.message);
      await fetchBotStatus();
      if (action === 'stop') {
        await fetchTradeHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to control bot');
    } finally {
      setLoading(false);
    }
  };

  const handleManualBuy = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/bot/manual/buy`);
      toast.success(response.data.message);
      await fetchBotStatus();
      await fetchTradeHistory(); // Refresh trade history
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to execute manual buy';
      // Check if it's a credit-related error
      if (errorMsg.toLowerCase().includes('credit') || errorMsg.toLowerCase().includes('insufficient')) {
        toast.error(
          <div>
            <p>{errorMsg}</p>
            <a href="/buy-credits" className="text-green-400 underline mt-2 block">
              → Purchase Credits
            </a>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualSell = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/bot/manual/sell`);
      toast.success(response.data.message);
      await fetchBotStatus();
      await fetchTradeHistory();
      // Refresh wallet to show updated credits after commission deduction
      await refreshWallet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute manual sell');
    } finally {
      setLoading(false);
    }
  };

  const handleManualShort = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/bot/manual/short`);
      toast.success(response.data.message);
      await fetchBotStatus();
      await fetchTradeHistory();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to execute manual short';
      // Check if it's a credit-related error
      if (errorMsg.toLowerCase().includes('credit') || errorMsg.toLowerCase().includes('insufficient')) {
        toast.error(
          <div>
            <p>{errorMsg}</p>
            <a href="/buy-credits" className="text-green-400 underline mt-2 block">
              → Purchase Credits
            </a>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalPnL = () => {
    return tradeHistory.reduce((sum, trade) => sum + (trade.pnl || 0), 0);
  };

  const calculateWinRate = () => {
    if (tradeHistory.length === 0) return 0;
    const wins = tradeHistory.filter(trade => trade.pnl > 0).length;
    return ((wins / tradeHistory.length) * 100).toFixed(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="container mx-auto px-4 py-8">
        {/* User Header Bar */}
        <div className="flex justify-between items-center mb-6 p-3 bg-white/5 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <span className="text-white font-medium hidden sm:inline">{user?.name || user?.email}</span>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <Wallet className="w-4 h-4" />
              <span className="font-semibold">{wallet?.balance_credits?.toFixed(0) || 0}</span>
              <span className="text-green-300 text-sm">credits</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/download">
              <Button variant="ghost" size="sm" className="text-cyan-300 hover:bg-cyan-500/20" data-testid="download-nav">
                <Monitor className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Desktop</span>
              </Button>
            </Link>
            <Link to="/help">
              <Button variant="ghost" size="sm" className="text-blue-300 hover:bg-blue-500/20" data-testid="help-nav">
                <HelpCircle className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Help</span>
              </Button>
            </Link>
            <Link to="/buy-credits">
              <Button variant="ghost" size="sm" className="text-green-300 hover:bg-green-500/20" data-testid="buy-credits-nav">
                <CreditCard className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Buy Credits</span>
              </Button>
            </Link>
            <Link to="/referrals">
              <Button variant="ghost" size="sm" className="text-purple-300 hover:bg-purple-500/20" data-testid="earn-credits-nav">
                <TrendingUp className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Earn Free</span>
              </Button>
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="text-yellow-300 hover:bg-yellow-500/20" data-testid="admin-nav">
                  <Shield className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Admin</span>
                </Button>
              </Link>
            )}
            <Link to="/account">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                <Settings className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Account</span>
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={logout} className="text-red-300 hover:bg-red-500/20">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2" data-testid="dashboard-title">
              OMNITrade Crypto Trading Bot
            </h1>
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
              <p className="text-blue-200 text-sm sm:text-base" data-testid="dashboard-subtitle">
                {getStrategyName(botStatus?.strategy || botSettings?.strategy)}
              </p>
              <span className="text-xs text-blue-300/70 whitespace-nowrap">
                Last updated: {lastUpdate.toLocaleTimeString()}
              </span>
            </div>
          </div>
          
          {/* Control Buttons - Responsive Layout */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full lg:w-auto">
            {/* Manual Trading Controls */}
            {botStatus?.is_running && (
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  onClick={handleManualBuy}
                  disabled={loading || botStatus?.current_position}
                  className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 text-sm"
                  data-testid="manual-buy-button"
                  title={botStatus?.current_position ? "Close current position first" : "Manually open LONG position"}
                >
                  <TrendingUp className="mr-1 sm:mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Manual</span> BUY
                </Button>
                <Button
                  onClick={handleManualShort}
                  disabled={loading || botStatus?.current_position}
                  className="flex-1 sm:flex-none bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 text-sm"
                  data-testid="manual-short-button"
                  title={botStatus?.current_position ? "Close current position first" : "Manually open SHORT position"}
                >
                  <TrendingDown className="mr-1 sm:mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Manual</span> SHORT
                </Button>
                <Button
                  onClick={handleManualSell}
                  disabled={loading || !botStatus?.current_position}
                  className="flex-1 sm:flex-none bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 text-sm"
                  data-testid="manual-sell-button"
                  title={!botStatus?.current_position ? "No position to close" : "Manually close current position"}
                >
                  <TrendingDown className="mr-1 sm:mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Close</span> POS
                </Button>
              </div>
            )}
            
            {/* Bot Control Buttons */}
            <div className="flex gap-2 w-full sm:w-auto">
              {botStatus?.is_running ? (
                <>
                  {botStatus?.is_paused ? (
                    <Button
                      onClick={() => handleBotControl('resume')}
                      disabled={loading}
                      className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white text-sm"
                      data-testid="resume-bot-button"
                    >
                      <Play className="mr-1 sm:mr-2 h-4 w-4" />
                      Resume
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleBotControl('pause')}
                      disabled={loading}
                      className="flex-1 sm:flex-none bg-yellow-600 hover:bg-yellow-700 text-white text-sm"
                      data-testid="pause-bot-button"
                    >
                      <Activity className="mr-1 sm:mr-2 h-4 w-4" />
                      Pause
                    </Button>
                  )}
                  <Button
                    onClick={() => handleBotControl('stop')}
                    disabled={loading}
                    className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white text-sm"
                    data-testid="stop-bot-button"
                  >
                    <Square className="mr-1 sm:mr-2 h-4 w-4" />
                    Stop
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => handleBotControl('start')}
                  disabled={loading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white text-sm"
                  data-testid="start-bot-button"
                >
                  <Play className="mr-1 sm:mr-2 h-4 w-4" />
                  Start Bot
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Paused Alert */}
        {botStatus?.is_paused && (
          <Alert className="mb-6 bg-yellow-500/20 border-yellow-500/50 text-yellow-100" data-testid="paused-alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Bot is PAUSED</strong> - Monitoring prices and managing open positions (stop-loss/take-profit still active), but won't open new positions or close based on signals. Click "Resume Bot" to restore normal trading.
            </AlertDescription>
          </Alert>
        )}

        {/* Live Trading Connection Issue Alert */}
        {!botStatus?.paper_trading && botStatus?.is_running && (botStatus?.live_balance_usd === null || botStatus?.live_balance_usd === undefined) && (
          <Alert className="mb-6 bg-red-500/20 border-red-500/50 text-red-100" data-testid="invalid-api-alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Exchange Connection Issue</strong> - Unable to connect to {botStatus?.exchange?.toUpperCase() || 'the exchange'} API. This could be due to:
              <ul className="list-disc ml-6 mt-1">
                <li>Invalid or expired API credentials</li>
                <li>Geographic restrictions (some exchanges block certain regions)</li>
                <li>Exchange API maintenance</li>
              </ul>
              Please verify your API keys in <Link to="/account" className="underline font-semibold">Account</Link> or enable <strong>Paper Trading</strong> mode to test without real credentials.
            </AlertDescription>
          </Alert>
        )}

        {/* Insufficient Balance Alert */}
        {botStatus?.needs_credits && (
          <Alert className="mb-6 bg-red-500/20 border-red-500/50 text-red-100" data-testid="needs-credits-alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div>
                  <strong>Insufficient Credits</strong> - You need more credits to continue trading.
                  <span className="block mt-1 text-sm">
                    Your credit balance is too low to open new positions. Purchase credits to resume live trading.
                  </span>
                </div>
                <Link to="/buy-credits">
                  <Button className="ml-4 bg-green-600 hover:bg-green-700 text-white">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Buy Credits
                  </Button>
                </Link>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {botStatus?.last_trade_error && !botStatus?.needs_credits && (
          <Alert className="mb-6 bg-yellow-500/20 border-yellow-500/50 text-yellow-100" data-testid="trade-error-alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Trade Error</strong> - {botStatus.last_trade_error}
              {botStatus.last_trade_error.toLowerCase().includes('insufficient') && (
                <span className="block mt-1 text-sm">
                  Your exchange balance (${botStatus?.live_balance_usd?.toFixed(2) || '0.00'}) is too low for the configured position size. 
                  Either add funds to your exchange account or use <strong>Paper Trading</strong> mode.
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="status-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-blue-100">Bot Status</CardTitle>
                {(botStatus?.exchange || botSettings?.exchange) && (
                  <Badge className="bg-purple-600" data-testid="exchange-badge">
                    {(botStatus?.exchange || botSettings?.exchange).toUpperCase()}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Activity className={`h-5 w-5 ${botStatus?.is_running && !botStatus?.is_paused ? 'text-green-400 animate-pulse' : botStatus?.is_paused ? 'text-yellow-400' : 'text-gray-400'}`} />
                <span className="text-2xl font-bold text-white">
                  {botStatus?.is_paused ? 'Paused' : botStatus?.is_running ? 'Running' : 'Stopped'}
                </span>
              </div>
              {(botStatus || botSettings) && (
                <div className="mt-2 space-y-1">
                  <Badge 
                    className={(botStatus?.paper_trading ?? botSettings?.paper_trading) ? 'bg-blue-500' : 'bg-orange-500'} 
                    data-testid="trading-mode-badge"
                  >
                    {(botStatus?.paper_trading ?? botSettings?.paper_trading) ? 'Paper Trading' : 'Live Trading'}
                  </Badge>
                  {botStatus?.is_paused && (
                    <Badge className="ml-2 bg-yellow-500" data-testid="paused-badge">
                      Monitoring Only
                    </Badge>
                  )}
                </div>
              )}
              {botStatus?.is_running && (
                <div className="mt-2 flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${botStatus.websocket_connected ? 'bg-green-400 animate-pulse' : 'bg-yellow-400'}`}></div>
                  <span className="text-xs text-blue-200">
                    {botStatus.websocket_connected ? 'Real-time prices' : 'Connecting...'}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="balance-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-100">Portfolio Balance</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Total Balance (USD + Crypto converted to USD) */}
              {(() => {
                const usdBalance = botStatus?.paper_trading 
                  ? (botStatus?.paper_balance_usd || 0)
                  : (botStatus?.live_balance_usd || 0);
                const cryptoBalance = botStatus?.paper_trading
                  ? (botStatus?.paper_balance_crypto || 0)
                  : (botStatus?.live_balance_crypto || 0);
                const currentPrice = botStatus?.current_price || 0;
                const cryptoValueUsd = cryptoBalance * currentPrice;
                const totalUsd = usdBalance + cryptoValueUsd;
                
                const hasValidData = botStatus?.paper_trading 
                  ? (botStatus?.paper_balance_usd !== null && botStatus?.paper_balance_usd !== undefined)
                  : (botStatus?.live_balance_usd !== null && botStatus?.live_balance_usd !== undefined);
                
                return (
                  <>
                    {/* Total Portfolio Value */}
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign className="h-5 w-5 text-green-400" />
                      <div>
                        <span className="text-xl font-bold text-white" data-testid="total-balance">
                          {hasValidData
                            ? `$${totalUsd.toFixed(2)}`
                            : !botStatus?.paper_trading && botStatus?.is_running
                            ? 'API Error'
                            : '...'}
                        </span>
                        {hasValidData && (
                          <span className="text-xs text-blue-300 ml-1">total</span>
                        )}
                      </div>
                    </div>
                    
                    {/* USD and Crypto Breakdown */}
                    {hasValidData && (
                      <div className="border-t border-white/10 pt-2 space-y-1">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-blue-200">USD</span>
                          <span className="text-white font-medium" data-testid="usd-balance">
                            ${usdBalance.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-blue-200">
                            {botSettings?.symbol 
                              ? botSettings.symbol.replace('t', '').replace('USD', '').replace('USDT', '').replace('-', '').replace('_', '')
                              : 'Crypto'}
                          </span>
                          <div className="text-right">
                            <span className="text-white font-medium" data-testid="crypto-balance">
                              {cryptoBalance.toFixed(6)}
                            </span>
                            {currentPrice > 0 && (
                              <span className="text-blue-300 text-xs ml-1">
                                (${cryptoValueUsd.toFixed(2)})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              
              {!botStatus?.paper_trading && botStatus?.is_running && (botStatus?.live_balance_usd === null || botStatus?.live_balance_usd === undefined) && (
                <div className="mt-2 text-xs text-red-300">
                  Invalid API keys or connection issue
                </div>
              )}
              {!botStatus?.paper_trading && !botStatus?.is_running && (
                <div className="mt-2 text-xs text-blue-300">
                  Start bot to fetch balances
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="price-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-100 flex items-center justify-between">
                <span>{botSettings?.symbol ? getCryptoName(botSettings.symbol) : 'Crypto'} Price</span>
                {botStatus?.current_position && (
                  <Badge variant="outline" className={`text-xs ${botStatus.current_position.type === 'LONG' ? 'border-green-500 text-green-300' : 'border-red-500 text-red-300'}`}>
                    {botStatus.current_position.type === 'LONG' ? 'Bid' : 'Ask'}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white" data-testid="btc-price">
                {botStatus?.current_price ? formatPrice(botStatus.current_price) : '...'}
              </div>
              {/* Show bid/ask spread when available */}
              {botStatus?.bid_price && botStatus?.ask_price && (
                <div className="mt-2 text-xs text-blue-300 flex gap-4">
                  <span>Bid: {formatPrice(botStatus.bid_price)}</span>
                  <span>Ask: {formatPrice(botStatus.ask_price)}</span>
                </div>
              )}
              {botStatus?.last_signal && (
                <div className="mt-2 text-sm text-blue-200" data-testid="last-signal">
                  Signal: <span className="font-semibold">{botStatus.last_signal.action}</span>
                  {botStatus.last_signal.rsi && (
                    <div className="text-xs mt-1">
                      RSI: {botStatus.last_signal.rsi.toFixed(2)}
                    </div>
                  )}
                </div>
              )}
              {botStatus?.price_update_count > 0 && (
                <div className="mt-2 text-xs text-blue-300">
                  Updates: {botStatus.price_update_count}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="position-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-100">Current Position</CardTitle>
            </CardHeader>
            <CardContent>
              {botStatus?.current_position ? (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {botStatus.current_position.type === 'LONG' ? (
                      <TrendingUp className="h-5 w-5 text-green-400" />
                    ) : (
                      <TrendingDown className="h-5 w-5 text-red-400" />
                    )}
                    <span className="text-2xl font-bold text-white">
                      {botStatus.current_position.type}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="text-blue-200">
                      Entry: {formatPrice(botStatus.current_position.entry_price)}
                    </div>
                    <div className="text-blue-200">
                      Amount: {botStatus.current_position.amount.toFixed(6)}
                    </div>
                    <div className={`font-semibold ${botStatus.current_position.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="current-pnl">
                      PnL: ${botStatus.current_position.pnl.toFixed(6)} ({botStatus.current_position.pnl_pct.toFixed(2)}%)
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-2xl font-bold text-gray-400">No Position</div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="pnl-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-100">Total P&L</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-1">
                <span className={`text-2xl font-bold ${calculateTotalPnL() >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="total-pnl">
                  ${calculateTotalPnL().toFixed(2)}
                </span>
              </div>
              <div className="mt-2 space-y-1 text-sm">
                <div className="text-blue-200">
                  Trades: {tradeHistory.length}
                </div>
                <div className="text-blue-200">
                  Win Rate: {calculateWinRate()}%
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Strategy Indicators Panel */}
        {botStatus?.is_running && botStatus?.strategy_indicators && (
          <div className="mb-8">
            <StrategyIndicators 
              indicators={botStatus.strategy_indicators} 
              strategy={botStatus.strategy}
              currentPrice={botStatus.current_price}
            />
          </div>
        )}

        {/* Risk Management Summary */}
        {botStatus?.daily_loss_summary && (
          <Card className="mb-8 bg-white/10 border-white/20 backdrop-blur-sm" data-testid="risk-summary-card">
            <CardHeader>
              <CardTitle className="text-white">Daily Performance Summary</CardTitle>
              <CardDescription className="text-blue-200">Today's trading performance and limits</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <div className="text-sm text-blue-200 mb-1">Net P&L Today</div>
                  <div className={`text-2xl font-bold ${botStatus.daily_loss_summary.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${botStatus.daily_loss_summary.total_pnl?.toFixed(2) || '0.00'}
                  </div>
                  <div className="text-xs text-blue-300 mt-1">
                    Profit: ${botStatus.daily_loss_summary.total_profit?.toFixed(2) || '0.00'} | 
                    Loss: ${botStatus.daily_loss_summary.total_loss?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-blue-200 mb-1">Loss for Limit</div>
                  <div className="text-2xl font-bold text-white">
                    ${botStatus.daily_loss_summary.net_loss?.toFixed(2) || '0.00'}
                  </div>
                  <div className="text-xs text-blue-300 mt-1">
                    Only net losses count
                  </div>
                </div>
                <div>
                  <div className="text-sm text-blue-200 mb-1">Daily Limit</div>
                  <div className="text-2xl font-bold text-white">
                    ${botStatus.daily_loss_summary.limit?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-blue-200 mb-1">Remaining</div>
                  <div className={`text-2xl font-bold ${botStatus.daily_loss_summary.limit_reached ? 'text-red-400' : 'text-green-400'}`}>
                    ${botStatus.daily_loss_summary.remaining?.toFixed(2) || '0.00'}
                  </div>
                </div>
              </div>
              {botStatus.daily_loss_summary.limit_reached && (
                <Alert className="mt-4 bg-red-500/20 border-red-500/50 text-red-100">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Daily loss limit reached. Bot will not open new positions until tomorrow.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Trade History */}
        <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="trade-history-card">
          <CardHeader>
            <CardTitle className="text-white">Trade History</CardTitle>
            <CardDescription className="text-blue-200">Recent completed trades</CardDescription>
          </CardHeader>
          <CardContent>
            {tradeHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-400" data-testid="no-trades-message">
                No trades yet. Start the bot to begin trading.
              </div>
            ) : (
              <div className="space-y-3">
                {tradeHistory.slice().reverse().map((trade, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-lg bg-white/5 border border-white/10"
                    data-testid={`trade-${index}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={trade.type === 'LONG' ? 'bg-green-600' : 'bg-red-600'}>
                            {trade.type}
                          </Badge>
                          <span className="text-sm text-blue-200">
                            {new Date(trade.entry_time).toLocaleString()}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <div className="text-blue-200">Entry</div>
                            <div className="text-white font-semibold">{formatPrice(trade.entry_price)}</div>
                          </div>
                          <div>
                            <div className="text-blue-200">Exit</div>
                            <div className="text-white font-semibold">{formatPrice(trade.exit_price)}</div>
                          </div>
                          <div>
                            <div className="text-blue-200">Amount</div>
                            <div className="text-white font-semibold">{trade.amount.toFixed(2)}</div>
                          </div>
                          <div>
                            <div className="text-blue-200">P&L</div>
                            <div className={`font-semibold ${trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              ${trade.pnl.toFixed(6)} ({trade.pnl_pct.toFixed(2)}%)
                            </div>
                          </div>
                        </div>
                        {trade.close_reason && (
                          <div className="mt-2 text-xs text-blue-300">
                            Reason: {trade.close_reason}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
